import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export const fetchSuppliers = createAsyncThunk(
  "supplier/fetchSuppliers",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any[];
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search.trim());
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);

      const body = {
        filters: filterPayload,
      };

      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.SUPPLIER?.LIST}?${params.toString()}`,
        body,
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      throw new Error("Failed to fetch supplier");
    }
  },
);

export const exportSupplierCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: {
    filterField: string;
    filterValue: any;
  }[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  // -----------------------------
  // Build structured filters body
  // -----------------------------
  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await Axios.post(
    `${API_ENDPOINT.MASTERS.SUPPLIER.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" },
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `suppliers_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

export const fetchSupplierById = createAsyncThunk(
  "supplier/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(
        `${API_ENDPOINT.MASTERS.SUPPLIER.DETAIL(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch supplier by ID",
      );
    }
  },
);

export const fetchSupplierDropDown = createAsyncThunk(
  "supplier/fetchByDropDown",
  async (
    payload: { product_id?: string[] } | undefined,
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      if (payload?.product_id !== undefined) {
        if (payload.product_id.length === 0) {
          params.append("product_id", "");
        } else {
          payload.product_id.forEach((id) => params.append("product_id", id));
        }
      }
      const query = params.toString();
      const url = query
        ? `${API_ENDPOINT?.MASTERS?.SUPPLIER?.DROPDOWN}?${query}`
        : `${API_ENDPOINT?.MASTERS?.SUPPLIER?.DROPDOWN}`;
      const response = await Axios.get(url);
      return response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Supplier list",
      );
    }
  },
);

export const fetchSupplierDropDownByPR = createAsyncThunk(
  "supplier/fetchByDropDownByPR",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.SUPPLIER?.DROPDOWN_BY_PR}`,
      );
      return response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Supplier list (by PR)",
      );
    }
  },
);

export const fetchSupplyableProducts = createAsyncThunk(
  "supplier/fetchSupplyableProducts",
  async (
    payload: { supplier_id: string; product_id: string[] },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.append("supplier_id", payload.supplier_id);
      if (payload.product_id.length === 0) {
        params.append("product_id", "");
      } else {
        payload.product_id.forEach((id) => params.append("product_id", id));
      }
      const url = `${API_ENDPOINT?.MASTERS?.SUPPLIER?.SUPPLYABLE_PRODUCTS}?${params.toString()}`;
      const response = await Axios.get(url);
      return response?.data ?? { data: [] };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch supplyable products",
      );
    }
  },
);

export const updateSupplier = createAsyncThunk(
  "supplier/updateSupplier",
  async (data: any, { rejectWithValue }) => {
    try {
      const { id, formData } = data;
      const response: any = await Axios.put(
        `${API_ENDPOINT.MASTERS.SUPPLIER.UPDATE(id)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update supplier");
    }
  },
);

export const updateStatusInSupplier = createAsyncThunk(
  "supplier/updateStatusInSupplier",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.patch(
        `${API_ENDPOINT.MASTERS.SUPPLIER.STATUS_UPDATE(id)}`,
      );
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update status in supplier",
      );
    }
  },
);

export const addSupplier = createAsyncThunk(
  "supplier/addSupplier",
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(
        `${API_ENDPOINT?.MASTERS?.SUPPLIER?.CREATE}`,
        data,
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add supplier");
    }
  },
);

export const fetchBranchDropdownBySupplierID = createAsyncThunk(
  "supplier/fetchBranchDropdownBySupplierID",
  async (supplier_id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(
        `${API_ENDPOINT?.MASTERS?.SUPPLIER?.BRANCH_DROPDOWN_BY_SUPPLIER_ID(supplier_id)}`,
      );
      return response?.data || [];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch Supplier list",
      );
    }
  },
);
